import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Eye, ShieldCheck, Loader2, MailCheck, AlertTriangle, Users } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { PageHeader } from "@/components/app/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Outreach() {
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const canSend = hasRole("admin", "manager");

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [targetType, setTargetType] = useState("tag");
  const [tag, setTag] = useState("");
  const [segmentId, setSegmentId] = useState("");
  const [preview, setPreview] = useState(null);
  const [approved, setApproved] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data: tags } = useQuery({ queryKey: ["contactTags"], queryFn: () => api.get("/contacts/tags").then((r) => r.data) });
  const { data: segments } = useQuery({ queryKey: ["segments"], queryFn: () => api.get("/audience/segments").then((r) => r.data) });
  const { data: sends } = useQuery({ queryKey: ["sends"], queryFn: () => api.get("/outreach/sends").then((r) => r.data) });

  const payload = () => ({
    subject, body, target_type: targetType,
    tag: targetType === "tag" ? tag : undefined,
    segment_id: targetType === "segment" ? segmentId : undefined,
  });

  const doPreview = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Add a subject and body");
    setBusy(true);
    try {
      const { data } = await api.post("/outreach/preview", payload());
      setPreview(data);
      setApproved(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const doSend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/outreach/send", { ...payload(), previewed: true, approved: true });
      toast.success(`Simulated send to ${data.recipient_count} contacts`);
      setPreview(null); setApproved(false); setSubject(""); setBody("");
      qc.invalidateQueries({ queryKey: ["sends"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title="Email & Press"
        subtitle="Send to opt-in contacts only, never the voter file. Preview is mandatory and a manager must approve before anything goes out."
        icon={Send}
      />

      <div className="mb-4 flex items-center gap-2 rounded-lg border border-neu-border bg-neu-bg px-4 py-2.5 text-sm text-neu-text">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span><strong>Simulated mode:</strong> sends are recorded but not delivered until a SendGrid key is connected in Settings → Integrations.</span>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]">
        {/* Composer */}
        <div className="space-y-4 rounded-xl border border-border bg-white p-6 shadow-sm">
          <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Join us canvassing this Saturday" className="mt-1.5" data-testid="email-subject-input" /></div>
          <div><Label>Body</Label><Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} placeholder="Hi {first name}, …" className="mt-1.5" data-testid="email-body-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Audience</Label>
              <Select value={targetType} onValueChange={(v) => { setTargetType(v); setPreview(null); }}>
                <SelectTrigger className="mt-1.5" data-testid="audience-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tag">Contacts by tag</SelectItem>
                  <SelectItem value="segment">Segment (contact subset)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{targetType === "tag" ? "Tag" : "Segment"}</Label>
              {targetType === "tag" ? (
                <Select value={tag} onValueChange={(v) => { setTag(v); setPreview(null); }}>
                  <SelectTrigger className="mt-1.5" data-testid="tag-select"><SelectValue placeholder="Choose tag" /></SelectTrigger>
                  <SelectContent>{tags?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Select value={segmentId} onValueChange={(v) => { setSegmentId(v); setPreview(null); }}>
                  <SelectTrigger className="mt-1.5" data-testid="segment-select"><SelectValue placeholder="Choose segment" /></SelectTrigger>
                  <SelectContent>{segments?.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          </div>
          <Button variant="outline" onClick={doPreview} disabled={busy} data-testid="preview-email-btn" className="w-full">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />} Preview send
          </Button>
        </div>

        {/* Preview / approval */}
        <div className="space-y-4">
          {!preview ? (
            <div className="flex h-full min-h-[260px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-white/60 p-8 text-center">
              <MailCheck className="mb-3 h-8 w-8 text-brand-blue" />
              <p className="font-heading font-bold text-brand-ink">Preview before you send</p>
              <p className="mt-1 text-sm text-brand-mute">We'll resolve the opt-in recipients and append the required footer.</p>
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-brand-ink">Send preview</h3>
                <Badge className="bg-brand-blue/10 text-brand-blue"><Users className="mr-1 h-3 w-3" /> {preview.recipient_count} recipients</Badge>
              </div>
              <p className="mt-1 text-xs text-brand-mute">{preview.note}</p>
              <div className="mt-3 rounded-lg border border-border bg-slate-50 p-3">
                <p className="text-sm font-semibold text-brand-ink">{preview.subject}</p>
                <pre className="mt-2 max-h-52 overflow-y-auto whitespace-pre-wrap font-body text-xs leading-relaxed text-brand-ink">{preview.composed_body}</pre>
              </div>
              <p className="mt-2 flex items-center gap-1.5 text-xs text-pos-text"><ShieldCheck className="h-3.5 w-3.5" /> Unsubscribe + disclaimer included · opt-in contacts only</p>

              {canSend ? (
                preview.recipient_count > 0 ? (
                  <div className="mt-4 space-y-3 border-t border-border pt-4">
                    <label className="flex cursor-pointer items-start gap-2 text-sm text-brand-ink">
                      <Checkbox checked={approved} onCheckedChange={setApproved} data-testid="approve-send-checkbox" className="mt-0.5" />
                      I approve this send to {preview.recipient_count} opt-in contacts.
                    </label>
                    <Button onClick={doSend} disabled={!approved || busy} className="w-full bg-brand-red text-white hover:bg-brand-redhover" data-testid="send-email-btn">
                      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Approve & send (simulated)
                    </Button>
                  </div>
                ) : (
                  <p className="mt-4 border-t border-border pt-4 text-sm text-brand-mute">No opt-in contacts match this audience.</p>
                )
              ) : (
                <p className="mt-4 border-t border-border pt-4 text-sm text-brand-mute">Only Admin or Campaign Manager can approve sends.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* History */}
      {sends?.length > 0 && (
        <div className="mt-8 overflow-hidden rounded-xl border border-border bg-white shadow-sm">
          <div className="border-b border-border px-5 py-3"><h3 className="font-heading text-sm font-bold text-brand-ink">Send history</h3></div>
          <Table>
            <TableHeader><TableRow><TableHead>Subject</TableHead><TableHead>Audience</TableHead><TableHead>Recipients</TableHead><TableHead>Opens</TableHead><TableHead>Clicks</TableHead><TableHead>Status</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
            <TableBody>
              {sends.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium text-brand-ink">{s.subject}</TableCell>
                  <TableCell className="text-xs text-brand-mute">{s.target_descriptor}</TableCell>
                  <TableCell>{s.recipient_count}</TableCell>
                  <TableCell>{s.opens}</TableCell>
                  <TableCell>{s.clicks}</TableCell>
                  <TableCell><Badge variant="outline" className="bg-neu-bg text-neu-text">simulated</Badge></TableCell>
                  <TableCell className="text-xs text-brand-mute">{timeAgo(s.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
